import React, { useMemo, useState } from 'react';
import { Card, Table, Typography, Button, Space, Progress, Input, Tag, Row, Col, Select, Statistic } from 'antd';
import { SortAscendingOutlined, SortDescendingOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import useAllEmployees from '../../../hooks/useAllEmployees';
import { keyBy } from 'lodash';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * DriverPerformance Component
 * Displays detailed driver performance metrics from Transport for NSW Trip Passing Time Reports
 */
const DriverPerformance = ({ reportData }) => {
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [minTrips, setMinTrips] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(10);

  const {allEmployees} = useAllEmployees();

  const allEmployeesByEmployeeId = useMemo(() => {
    console.log('allEmployees', allEmployees, keyBy(allEmployees, 'employeeNumber'));
    return keyBy(allEmployees, 'employeeNumber');
  }, [allEmployees]);

  // If no data, show empty state
  if (!reportData || !reportData.driverPerformance) {
    return (
      <Card title="Driver Performance">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="secondary">No driver performance data available. Please generate a report.</Text>
        </div>
      </Card>
    );
  }

  // Get the drivers array - handle both formats (direct array or drivers property)
  const driverPerformanceData = reportData.driverPerformance;
  const drivers = Array.isArray(driverPerformanceData) 
    ? driverPerformanceData 
    : (driverPerformanceData.drivers || []);
  
  if (drivers.length === 0) {
    return (
      <Card title="Driver Performance">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="secondary">No driver data available for analysis.</Text>
        </div>
      </Card>
    );
  }

  // Enhance drivers with employee information
  const enhancedDrivers = drivers.map(driver => {
    // Try to find matching employee using driverId as employeeId
    const employeeMatch = allEmployeesByEmployeeId[driver.driverId];
    console.log('employeeMatch', employeeMatch);
    console.log('driver', driver);
    console.log('allEmployeesByEmployeeId', allEmployeesByEmployeeId);
    
    return {
      ...driver,
      // Add employee information if found
      employeeName: employeeMatch? `${employeeMatch?.firstName} ${employeeMatch?.lastName}` : null,
      employee: employeeMatch || null
    };
  });

  // Format percentage values
  const formatPercent = (value) => {
    if (value === undefined || value === null || isNaN(value)) return '0%';
    return `${Math.min(value, 100).toFixed(2)}%`;
  };

  // Filter and sort drivers
  const filteredDrivers = enhancedDrivers
    .filter(driver => {
      // Apply search filter - check both driver ID and employee name
      if (searchTerm && 
          !driver.driverId.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !(driver.employeeName && driver.employeeName.toLowerCase().includes(searchTerm.toLowerCase()))) {
        return false;
      }
      
      // Apply minimum trips filter
      if (minTrips > 0 && driver.totalTrips < minTrips) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      // Sort by OTR percentage
      return sortOrder === 'desc' 
        ? b.overallOTR - a.overallOTR 
        : a.overallOTR - b.overallOTR;
    });

  // Define table columns
  const columns = [
    {
      title: 'Driver',
      dataIndex: 'driverId',
      key: 'driverId',
      width: 180,
      fixed: 'left',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          {record.employeeName && (
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.employeeName}</Text>
          )}
        </Space>
      )
    },
    {
      title: 'Overall OTR',
      dataIndex: 'overallOTR',
      key: 'overallOTR',
      sorter: (a, b) => a.overallOTR - b.overallOTR,
      render: value => (
        <Progress 
          percent={parseFloat(value.toFixed(2))} 
          size="small" 
          status={value >= 90 ? 'success' : (value >= 80 ? 'normal' : value >= 70 ? 'active' : 'exception')}
          format={percent => `${percent}%`}
        />
      ),
      width: 150
    },
    {
      title: 'Trips',
      dataIndex: 'totalTrips',
      key: 'totalTrips',
      sorter: (a, b) => a.totalTrips - b.totalTrips,
      width: 80
    },
    {
      title: 'Routes',
      dataIndex: 'uniqueRoutes',
      key: 'uniqueRoutes',
      sorter: (a, b) => a.uniqueRoutes - b.uniqueRoutes,
      width: 80
    },
    {
      title: 'First Stop OTR',
      dataIndex: 'firstStopOTR',
      key: 'firstStopOTR',
      sorter: (a, b) => a.firstStopOTR - b.firstStopOTR,
      render: value => formatPercent(value),
      width: 120
    },
    {
      title: 'Middle Stop OTR',
      dataIndex: 'middleStopOTR',
      key: 'middleStopOTR',
      sorter: (a, b) => a.middleStopOTR - b.middleStopOTR,
      render: value => formatPercent(value),
      width: 120
    },
    {
      title: 'Last Stop OTR',
      dataIndex: 'lastStopOTR',
      key: 'lastStopOTR',
      sorter: (a, b) => a.lastStopOTR - b.lastStopOTR,
      render: value => formatPercent(value),
      width: 120
    },
    {
      title: 'Early %',
      dataIndex: 'earlyPercentage',
      key: 'earlyPercentage',
      sorter: (a, b) => a.earlyPercentage - b.earlyPercentage,
      render: value => (
        <Tag color={value < 10 ? 'green' : value < 20 ? 'blue' : 'geekblue'}>
          {formatPercent(value)}
        </Tag>
      ),
      width: 80
    },
    {
      title: 'Late %',
      dataIndex: 'latePercentage',
      key: 'latePercentage',
      sorter: (a, b) => a.latePercentage - b.latePercentage,
      render: value => (
        <Tag color={value < 10 ? 'green' : value < 20 ? 'orange' : 'red'}>
          {formatPercent(value)}
        </Tag>
      ),
      width: 80
    },
    {
      title: 'Very Late %',
      dataIndex: 'veryLatePercentage',
      key: 'veryLatePercentage',
      sorter: (a, b) => a.veryLatePercentage - b.veryLatePercentage,
      render: value => (
        <Tag color={value < 5 ? 'green' : value < 10 ? 'orange' : 'red'}>
          {formatPercent(value)}
        </Tag>
      ),
      width: 80
    },
    {
      title: 'Avg Variance',
      dataIndex: 'averageVariance',
      key: 'averageVariance',
      sorter: (a, b) => a.averageVariance - b.averageVariance,
      render: value => {
        const color = value < 0 ? 'blue' : value <= 60 ? 'green' : value <= 180 ? 'orange' : 'red';
        const plusSign = value > 0 ? '+' : '';
        return (
          <Tag color={color}>
            {plusSign}{value}s
          </Tag>
        );
      },
      width: 100
    },
    {
      title: 'Total Stops',
      dataIndex: 'totalStops',
      key: 'totalStops',
      sorter: (a, b) => a.totalStops - b.totalStops,
      width: 100
    }
  ];

  // Summary statistics
  const summaryStats = {
    totalDrivers: filteredDrivers.length,
    avgOTR: filteredDrivers.length > 0 
      ? filteredDrivers.reduce((sum, driver) => sum + driver.overallOTR, 0) / filteredDrivers.length 
      : 0,
    totalTrips: filteredDrivers.reduce((sum, driver) => sum + driver.totalTrips, 0),
    driversAbove80: filteredDrivers.filter(driver => driver.overallOTR >= 80).length,
    driversBelow70: filteredDrivers.filter(driver => driver.overallOTR < 70).length
  };

  // Get top and bottom drivers
  const topDrivers = [...filteredDrivers].sort((a, b) => b.overallOTR - a.overallOTR).slice(0, 3);
  const bottomDrivers = [...filteredDrivers].sort((a, b) => a.overallOTR - b.overallOTR).slice(0, 3);

  // Toggle sort order function
  const toggleSortOrder = () => {
    setSortOrder(prevOrder => prevOrder === 'desc' ? 'asc' : 'desc');
  };

  return (
    <div className="driver-performance">
      <Card 
        title={<Title level={4}>Driver Performance Analysis</Title>}
        className="performance-card"
        extra={
          <Space size="middle">
            <Input
              placeholder="Search drivers"
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
            <Select 
              placeholder="Min trips" 
              style={{ width: 120 }}
              value={minTrips > 0 ? minTrips : undefined}
              onChange={value => setMinTrips(value)}
              allowClear
            >
              <Option value={1}>1+ trips</Option>
              <Option value={5}>5+ trips</Option>
              <Option value={10}>10+ trips</Option>
              <Option value={25}>25+ trips</Option>
              <Option value={50}>50+ trips</Option>
            </Select>
            <Button 
              type="default"
              icon={sortOrder === 'desc' ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
              onClick={toggleSortOrder}
              title={`Sort ${sortOrder === 'desc' ? 'descending' : 'ascending'} by OTR`}
            >
              {sortOrder === 'desc' ? 'Highest OTR' : 'Lowest OTR'}
            </Button>
          </Space>
        }
      >
        {/* Summary statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic 
                title="Total Drivers" 
                value={summaryStats.totalDrivers} 
                prefix={<UserOutlined />}
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic 
                title="Average OTR" 
                value={formatPercent(summaryStats.avgOTR)}
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic 
                title={<span>Drivers <span style={{ color: 'green' }}>≥80%</span></span>}
                value={`${summaryStats.driversAbove80}/${summaryStats.totalDrivers}`}
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic 
                title={<span>Drivers <span style={{ color: 'red' }}>&lt;70%</span></span>}
                value={`${summaryStats.driversBelow70}/${summaryStats.totalDrivers}`}
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
        </Row>

        {/* Top and Bottom Performers */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12}>
            <Card 
              title="Top Performing Drivers" 
              size="small"
              style={{ height: '100%' }}
            >
              {topDrivers.map((driver, index) => (
                <div key={`top-${index}`} style={{ marginBottom: 12 }}>
                  <Space align="center">
                    <Tag color="green">{index + 1}</Tag>
                    <Text strong>
                      {driver.driverId}
                      {driver.employeeName && <span style={{ color: 'rgba(0,0,0,0.45)', marginLeft: 4, fontSize: '0.9em' }}>({driver.employeeName})</span>}
                    </Text>
                    <Progress 
                      percent={parseFloat(driver.overallOTR.toFixed(2))} 
                      size="small" 
                      status="success"
                      style={{ width: 180 }}
                      format={percent => `${percent}%`}
                    />
                    <Text>{formatPercent(driver.overallOTR)}</Text>
                  </Space>
                  <div style={{ marginLeft: 54, fontSize: '0.9em', color: 'rgba(0,0,0,0.45)' }}>
                    {driver.totalTrips} trips, {driver.uniqueRoutes} routes, avg variance: {driver.averageVariance > 0 ? '+' : ''}{driver.averageVariance}s
                  </div>
                </div>
              ))}
              {topDrivers.length === 0 && (
                <Text type="secondary">No driver data available</Text>
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card 
              title="Bottom Performing Drivers" 
              size="small"
              style={{ height: '100%' }}
            >
              {bottomDrivers.map((driver, index) => (
                <div key={`bottom-${index}`} style={{ marginBottom: 12 }}>
                  <Space align="center">
                    <Tag color="red">{index + 1}</Tag>
                    <Text strong>
                      {driver.driverId}
                      {driver.employeeName && <span style={{ color: 'rgba(0,0,0,0.45)', marginLeft: 4, fontSize: '0.9em' }}>({driver.employeeName})</span>}
                    </Text>
                    <Progress 
                      percent={parseFloat(driver.overallOTR.toFixed(2))} 
                      size="small" 
                      status={driver.overallOTR < 70 ? 'exception' : 'active'}
                      style={{ width: 180 }}
                      format={percent => `${percent}%`}
                    />
                    <Text>{formatPercent(driver.overallOTR)}</Text>
                  </Space>
                  <div style={{ marginLeft: 54, fontSize: '0.9em', color: 'rgba(0,0,0,0.45)' }}>
                    {driver.totalTrips} trips, {driver.uniqueRoutes} routes, avg variance: {driver.averageVariance > 0 ? '+' : ''}{driver.averageVariance}s
                  </div>
                </div>
              ))}
              {bottomDrivers.length === 0 && (
                <Text type="secondary">No driver data available</Text>
              )}
            </Card>
          </Col>
        </Row>

        {/* Drivers Table */}
        <Table
          dataSource={filteredDrivers.map((driver, index) => ({ ...driver, key: `driver-${index}` }))}
          columns={columns}
          pagination={{ 
            pageSize: tablePageSize,
            defaultPageSize: tablePageSize,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} drivers`,
            onShowSizeChange: (current, size) => setTablePageSize(size)
          }}
          scroll={{ x: 'max-content' }}
          size="small"
          bordered
        />
      </Card>
    </div>
  );
};

export default DriverPerformance; 